import { ShipmentTypes } from "src/Library/Types";
import { ModesOfService } from 'src/Library/Constants';
import GetExecutionData from 'src/dto/response/getExecution';
import { SHIPMENT_KEY_MAPPINGS } from "../Container/ContainerUtils";
import { isBreakBulkMode } from './SE.constants';
import { SEBLCardData, SEContainerCardData } from "./SE.interfaces";
import isEmpty from 'lodash/isEmpty';
import { isUndefined } from 'util';
const FCLIcon = require('src/assets/images/FCL.svg');
const LCLIcon = require('src/assets/images/LCL.svg');
const AirIcon = require('src/assets/images/Air.svg');
const BreakBulkIcon = require('src/assets/images/breakbulk-crane-crate-black.svg');

export const trackingCardData = (
  cardData: SEContainerCardData | SEBLCardData,
  cardType: ShipmentTypes
) => {
  const keys = SHIPMENT_KEY_MAPPINGS[cardType];
  const res = {
      refreshRequestedAt: cardData[keys.refreshRequestedAt],
      shipmentNumber: cardData[keys.shipmentNumber],
      trackingFetchFailureMessage: cardData[keys.trackingFetchFailureMessage],
      reachedDestinationAt: cardData[keys.reachedDestinationAt],
      routeFrom: cardData[keys.routeFrom],
      cardType,
      ...cardData,
    };
  return res;
};

export const trackingCardDataList = (
  cardDataList: (SEContainerCardData | SEBLCardData)[],
  cardType: ShipmentTypes
) => {
  return cardDataList?.map((cardData) => trackingCardData(cardData, cardType));
};

export type SETrackingCardData = ReturnType<typeof trackingCardData>;

export const uploadSIStepsMap = {
  STAKEHOLDER_AND_SERVICE_DETAILS: 0,
  CONTAINER_AND_CARGO: 1,
  ADDITIONAL_DETAILS: 2,
};

export const seTaskStatus = ['Not Started', 'In Progress', 'Done', 'Cancelled'];

export const siInttraStatusFilterList = [
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'REQUEST_NOT_RAISED', label: 'Request Not Raised' },
];

export const getSIPartyAddress = (address) => {
  if (isEmpty(address)) return '-';
  const { street, city, state, pincode, country } = address;
  const addressString = [street, city, state, pincode, country?.countryName]
    .filter(Boolean)
    .join(', ');

  return addressString;
};

export enum FORM13_CARGO_TYPES {
  GENERAL = 'GENERAL',
  HAZARDOUS = 'HAZARDOUS',
  REEFER = 'REEFER',
  ONION = 'ONION',
  ODC = 'ODC',
}

export const getForm13MandatoryFields = (props) => {
  const {
    portForm13FieldConfigMap,
    mandatoryStuffingLocationAttachments,
    shipmentData,
    stuffingLocation,
    cargoType,
  } = props;
  const polId = shipmentData?.originPolId || shipmentData?.originPortId;
  const getFieldValue = (key, defaultVal) => (isUndefined(key) ? defaultVal : key);
  const isFPodMandatory = getFieldValue(portForm13FieldConfigMap?.[polId]?.fpod, true);
  const isConsigneeNameMandatory = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.consigneeName,
    false
  );
  const isConsigneeAddressMandatory = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.consigneeAddress,
    false
  );
  const isCargoDescriptionMandatory = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.cargoDescription,
    false
  );
  const isTerminalLoginIdMandatory = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.terminalLoginId,
    false
  );
  const isPackageCountMandatory = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.packageCount,
    false
  );
  const isVehicleNoMandatory = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.stuffingLocationVehicle?.[stuffingLocation],
    false
  );
  const mandatoryStuffingAttachmentsBasedOnPOL = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.stuffingLocationAttachments?.[stuffingLocation],
    []
  );
  const mandatoryCarogTypeAttachments = getFieldValue(
    portForm13FieldConfigMap?.[polId]?.carogTypeAttachments?.[cargoType],
    []
  );
  const mandatoryStuffingAttachments = getFieldValue(
    mandatoryStuffingLocationAttachments?.[stuffingLocation],
    []
  );

  return {
    isFPodMandatory,
    isConsigneeNameMandatory,
    isConsigneeAddressMandatory,
    isCargoDescriptionMandatory,
    isTerminalLoginIdMandatory,
    isPackageCountMandatory,
    isVehicleNoMandatory,
    mandatoryStuffingAttachments,
    mandatoryCarogTypeAttachments,
    mandatoryStuffingAttachmentsBasedOnPOL,
  };
};

export const sideBarCollapsedPersistent = (value?: boolean) => {
  if (value === undefined) {
    return JSON.parse(window.localStorage.getItem('SE.sideBarCollapsed') || 'false');
  } else {
    window.localStorage.setItem('SE.sideBarCollapsed', JSON.stringify(value));
    return value;
  }
};


/** Modes that move by road, where the leg endpoints are addresses instead of ports. */
export const isRoadBasedMode = (mode?: string): boolean =>
  mode === ModesOfService.DOMESTIC || mode === ModesOfService.RPTL;

export const getModeIcon = (mode?: string) => {
  if (isBreakBulkMode(mode)) {
    return BreakBulkIcon;
  }
  switch (mode) {
    case ModesOfService.FCL:
      return FCLIcon;
    case ModesOfService.LCL:
    case ModesOfService.RPTL:
      return LCLIcon;
    default:
      return AirIcon;
  }
};

/**
 * Resolves the code/name/tooltip shown for one leg endpoint of a shipment.
 * Port based modes use the port code and name; road based modes fall back to the
 * location details, since those shipments have no port.
 */
export const getLegEndpointDisplay = (
  shipment: Pick<
    GetExecutionData,
    'mode' | 'originPortCode' | 'originPortName' | 'originLocationDetails'
    | 'destinationPortCode' | 'destinationPortName' | 'destinationLocationDetails'
  >,
  endpoint: 'origin' | 'destination'
) => {
  const isOrigin = endpoint === 'origin';
  const locationDetails = isOrigin
    ? shipment?.originLocationDetails
    : shipment?.destinationLocationDetails;
  let code = isOrigin ? shipment?.originPortCode : shipment?.destinationPortCode;
  let name = isOrigin ? shipment?.originPortName : shipment?.destinationPortName;
  let title = name;

  if (isRoadBasedMode(shipment?.mode)) {
    title = [
      locationDetails?.name,
      locationDetails?.address,
      locationDetails?.landmark,
      locationDetails?.city,
      locationDetails?.state,
      locationDetails?.countryName,
      locationDetails?.pincode,
    ].filter((val) => val).join(', ');
    code = locationDetails?.city?.toUpperCase() || locationDetails?.name?.toUpperCase();
    name = locationDetails?.pincode || locationDetails?.address;
  }

  return { title, code, name };
};

/** Total number of tasks on a shipment, summed across every task status bucket. */
export const getTaskSummaryTotal = (taskSummary?: Record<string, number>) => {
  const summary = taskSummary || {};
  const count = Object.keys(summary).reduce((acc, cur) => acc + summary[cur], 0);
  return count || '0';
};
